import { sanitizeHandoffNext } from '@imobdesk/auth-handoff'
import { API_BASE_URL } from '@/config/api'
import { getLoginAppOrigin } from '@/lib/login-app-url'
import { saveAccessToken } from '@/lib/token-storage'

const HANDOFF_TOKEN_SESSION_KEY = 'imobdesk_handoff_access_token'

function readAccessTokenFromHandoffUrl(): string | undefined {
  if (typeof window === 'undefined') return undefined
  const fromUrl = new URLSearchParams(window.location.search).get('access_token')
  if (fromUrl && fromUrl.length >= 20) return fromUrl
  return undefined
}

async function bootstrapRefreshCookieForApp(
  accessToken?: string,
): Promise<boolean> {
  const base = API_BASE_URL ? API_BASE_URL.replace(/\/$/, '') : ''
  const url = accessToken
    ? `${base}/api/v2/auth/bootstrap-refresh-cookie`
    : `${base}/api/v2/auth/refresh-token`
  try {
    const headers: Record<string, string> = { Accept: 'application/json' }
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`
    }
    const response = await fetch(url, {
      method: 'POST',
      headers,
      credentials: 'include',
    })
    if (!response.ok) {
      return false
    }
    if (!accessToken) {
      const contentType = response.headers.get('content-type')
      if (contentType?.includes('application/json')) {
        const data = (await response.json()) as { accessToken?: string }
        if (data?.accessToken) {
          saveAccessToken(data.accessToken)
        }
      }
    }
    return true
  } catch {
    return false
  }
}

/** Restaura token salvo no handoff antes do router (sobrevive ao reload). */
export function restoreHandoffTokenFromSessionStorage(): void {
  if (typeof window === 'undefined') return
  try {
    const token = sessionStorage.getItem(HANDOFF_TOKEN_SESSION_KEY)
    if (token && token.length >= 20) {
      saveAccessToken(token)
    }
    sessionStorage.removeItem(HANDOFF_TOKEN_SESSION_KEY)
  } catch {
    // ignore
  }
}

function stageHandoffTokenForReload(accessToken: string): void {
  try {
    sessionStorage.setItem(HANDOFF_TOKEN_SESSION_KEY, accessToken)
  } catch {
    // ignore
  }
  saveAccessToken(accessToken)
}

function markJustLoggedIn(): void {
  try {
    localStorage.setItem('imobdesk_just_logged_in', 'true')
    window.setTimeout(() => {
      try {
        localStorage.removeItem('imobdesk_just_logged_in')
      } catch {
        // ignore
      }
    }, 3000)
  } catch {
    // ignore
  }
}

function redirectToLoginAfterHandoffFailure(): void {
  const loginOrigin = getLoginAppOrigin()
  window.location.replace(
    loginOrigin
      ? `${loginOrigin}/?signed_out=1`
      : `${window.location.origin}/configuracao-login`,
  )
}

/**
 * Consome `/auth/handoff` antes do router montar — evita corrida com guards
 * que redirecionam ao login sem o access token da query.
 */
export async function applyAuthHandoffFromUrlIfNeeded(): Promise<boolean> {
  if (typeof window === 'undefined') return false
  if (window.location.pathname.replace(/\/$/, '') !== '/auth/handoff') {
    return false
  }

  const params = new URLSearchParams(window.location.search)
  const accessToken = readAccessTokenFromHandoffUrl()
  if (accessToken) {
    stageHandoffTokenForReload(accessToken)
  }

  const refreshed = await bootstrapRefreshCookieForApp(accessToken)
  if (!accessToken && !refreshed) {
    redirectToLoginAfterHandoffFailure()
    return true
  }

  markJustLoggedIn()
  const nextPath = sanitizeHandoffNext(params.get('next')) ?? '/workspace'
  window.location.replace(`${window.location.origin}${nextPath}`)
  return true
}
